import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FOLDER_DIRECTORIES } from '../../../constants/folder';
import useFoldersSplit from '../../../hooks/use-folders-split';
import translation from '../../../languages';
import { getFolders, triggerActionSuccessMessage } from '../../../reducers/folders';
import foldersService from '../../../services/folders/folders-service';
import qualityEvents from '../../../services/quality-events/quality-events';
import SharedModal from '../../../ui/modal/shared-modal';
import { storageKeys } from '../../../utils/browser-storage/storage-keys';
import cn from '../../../utils/classnames';
import Blinder from '../../blinder/blinder';
import MaterialCheckbox from '../../checkbox/material-checkbox';
import Icon from '../../icon/icon';
import InfoImg from '../../icon/info/info.green.svg';
import CreateFolderButton from '../create-folder-button/create-folder-button';
import './add-to-folders-modal.scss';

type AddToFolderModalProps = TranslationProps & {
    directory: FOLDER_DIRECTORIES;
    items: Material[];
    onClose: () => void;
    catalogVariant?: boolean;
    showCreateFolderBtn?: boolean;
};

const AddToFolderModal: FC<AddToFolderModalProps> = (props: AddToFolderModalProps) => {
    const { onClose, directory, items, t, catalogVariant, showCreateFolderBtn } = props;

    const dispatch = useDispatch();

    const { ownTree: foldersTree, ownList, sharedList, loaded } = useFoldersSplit(directory);
    const hasSharedFolders = directory === FOLDER_DIRECTORIES.MY_MATERIALS && sharedList.length > 0;

    const foldersListRef = useRef<HTMLDivElement | null>(null);

    const [noFoldersSelectedWarning, setNoFoldersSelectedWarning] = useState(false);
    const [APIError, setAPIError] = useState(false);
    const [pending, setPending] = useState(false);
    const [skipCatalogVariantAgain, setSkipCatalogVariantAgain] = useState(false);
    const [statusMap, setStatusMap] = useState({});

    const singleMaterialMode = items.length === 1;

    const isSaveDisabled = pending || !foldersTree.length;
    const handleSave = () => (isSaveDisabled ? undefined : save());

    const modalBtnConfig = catalogVariant
        ? [
              {
                  action: () => onClose(),
                  className: 'styled secondary',
                  text: t('modals.addToFolderModal.catalogVariant.buttons.skip')
              },
              {
                  action: handleSave,
                  className: `styled yellow ${isSaveDisabled ? 'disabled' : ''}`,
                  text: t('modals.addToFolderModal.catalogVariant.buttons.continue')
              }
          ]
        : [
              {
                  action: () => onClose(),
                  className: 'styled secondary',
                  text: t('modals.addToFolderModal.cancelBtn')
              },
              {
                  action: handleSave,
                  className: `styled yellow ${isSaveDisabled ? 'disabled' : ''}`,
                  text: t('modals.addToFolderModal.saveBtn')
              }
          ];

    const scrollToBottom = () => {
        if (foldersListRef.current) {
            const container = foldersListRef.current;

            if (container.scrollHeight > container.clientHeight) {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }
    };

    useEffect(() => {
        if (!foldersTree.length) {
            dispatch(getFolders(FOLDER_DIRECTORIES.WISHLIST));
        }

        if (foldersTree.length && singleMaterialMode) {
            const item = items[0];
            const status = {};

            setPending(true);

            foldersService.getFoldersByMaterialId(directory, item.id).then((list) => {
                const ownFolderIds = new Set(ownList.map((folder: Folder) => folder.id));

                // @ts-ignore
                list.forEach((folder: Folder) => {
                    if (ownFolderIds.has(folder.id)) {
                        status[folder.id] = true;
                    }
                });

                setStatusMap({
                    ...status
                });

                setPending(false);
            });
        }
    }, [foldersTree.length]);

    const save = () => {
        if (pending) {
            return;
        }

        const materialIds: number[] = items.map((item) => item.id);
        const selectedFolders: number[] = Object.keys(statusMap)
            .filter((key) => statusMap[key])
            .map((key) => Number(key));

        resetFormErrors();

        if (!singleMaterialMode && !selectedFolders.length && !catalogVariant) {
            setNoFoldersSelectedWarning(true);

            return;
        }

        setPending(true);
        let request;

        if (materialIds.length > 1) {
            request = foldersService
                .addMultipleMaterials({
                    folderIds: selectedFolders,
                    materialIds
                })
                .then(() => {
                    qualityEvents.myMaterialsPage.multiUpdateMaterialsFolders(selectedFolders, materialIds);
                });
        } else {
            request = foldersService
                .addSingleMaterial({
                    folderIds: selectedFolders,
                    folder_type: directory,
                    materialId: materialIds[0]
                })
                .then(() => {
                    qualityEvents.myMaterialsPage.updateMaterialFolders(selectedFolders);
                });
        }

        request.then(processResponseSuccess).catch(() => {
            setAPIError(true);
            setPending(false);
        });
    };

    const resetFormErrors = () => {
        setNoFoldersSelectedWarning(false);
        setAPIError(false);
    };

    const toggleFolderStatus = (folder: Folder) => {
        setStatusMap({
            ...statusMap,
            [folder.id]: !statusMap[folder.id]
        });
    };

    const processResponseSuccess = () => {
        dispatch(getFolders(directory, undefined, { invalidateCounts: true }));

        if (catalogVariant && skipCatalogVariantAgain) {
            window.storage.setItem(storageKeys.favorites.skipCatalogWishlistFolderModal, true);
        }

        if (!catalogVariant) {
            dispatch(triggerActionSuccessMessage(directory, t('search.results.messages.folderSet')));
        }

        onClose();
        setPending(false);
    };

    const handleCatalogCheckboxChange = () => {
        setSkipCatalogVariantAgain(!skipCatalogVariantAgain);
    };

    const renderStructure = (tree: Folder[]) => {
        return (
            <>
                {tree.map((folder: Folder) => {
                    return (
                        <div
                            key={folder.id}
                            className={cn('folder relative', {
                                subfolder: folder.parent_id
                            })}
                        >
                            <div>
                                <MaterialCheckbox
                                    checked={statusMap[folder.id]}
                                    type='checkbox'
                                    id={`folder-${folder.id}`}
                                    onChange={() => toggleFolderStatus(folder)}
                                    title={folder.title}
                                />
                            </div>
                            <div className='children'>{renderStructure(folder.children)}</div>
                        </div>
                    );
                })}
            </>
        );
    };

    const skipPopupBlock = () => {
        return catalogVariant ? (
            <div className='mt-2'>
                <MaterialCheckbox
                    checked={skipCatalogVariantAgain}
                    type='checkbox'
                    id='dont-show-again'
                    onChange={handleCatalogCheckboxChange}
                    title={t('modals.addToFolderModal.catalogVariant.dontShowAgain')}
                />
            </div>
        ) : null;
    };

    const onCreateFolder = () => {
        qualityEvents.myMaterialsPage.createFolderBtn();
    };

    const onCreateFolderSuccess = () => {
        setTimeout(scrollToBottom, 1000);
    };

    const modalBody = () => {
        return (
            <div className='mb-2 position-relative'>
                {(pending || !loaded) && <Blinder />}
                {hasSharedFolders && (
                    <div className='school-folders-info'>
                        <img src={InfoImg} className='school-folders-info-icon' alt='' />
                        <span>{t('modals.addToFolderModal.schoolFoldersInfo')}</span>
                    </div>
                )}
                {foldersTree.length ? (
                    <div>
                        <div className='folders-list-title'>{t('modals.addToFolderModal.subtitle')}</div>
                        <div ref={foldersListRef} className='folders-tree mb-2'>
                            {renderStructure(foldersTree)}
                        </div>
                        {showCreateFolderBtn && (
                            <CreateFolderButton
                                directory={directory}
                                onClickCallback={onCreateFolder}
                                onSuccessCallback={onCreateFolderSuccess}
                            >
                                <Icon name='plus' text={t('modals.addToFolderModal.createFolder')} height={18} />
                            </CreateFolderButton>
                        )}

                        {skipPopupBlock()}
                        {noFoldersSelectedWarning && (
                            <div className='mt-3 text-center warning'>
                                {t('modals.addToFolderModal.errors.selectFolders')}
                            </div>
                        )}
                        {APIError && (
                            <div className='mt-3 text-center error-message'>
                                {t('modals.addToFolderModal.errors.APIError')}
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <div className='no-folders-message mb-3'>{t('modals.addToFolderModal.noFolders')}</div>
                        <CreateFolderButton directory={directory} onClickCallback={onCreateFolder}>
                            <Icon name='plus' text={t('modals.addToFolderModal.createFolder')} height={18} />
                        </CreateFolderButton>
                        <div className='mt-4'>{skipPopupBlock()}</div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <SharedModal
            dialogClassName={cn('add-to-folder-modal', {
                'catalog-variant': catalogVariant
            })}
            backdropClassName='add-to-folder-modal-fade'
            containerClassName='add-to-folders-modal-container'
            onClose={onClose}
            enforceFocus={false}
            closeOnOutsideClick
            body={modalBody()}
            centered
            btnConfig={modalBtnConfig}
            title={
                <span className='tsc-h4'>
                    {catalogVariant
                        ? t('modals.addToFolderModal.catalogVariant.title')
                        : t('modals.addToFolderModal.title')}
                </span>
            }
        />
    );
};

const translate = translation('modals');

export default translate(AddToFolderModal);
